import { useEffect, useState, useCallback } from 'react';
import { Loader2, ExternalLink, Search, X, AlertTriangle, Link2 } from 'lucide-react';
import { docuwareFilingCheckApi } from '../../../services/docuwareFilingCheckApi';
import type { FilingCheckRow, FilingCheckDocument } from '../../../types/docuwareFilingCheck';
import { formatDate } from '../../../utils/format';

interface Props {
    row: FilingCheckRow;
    onClose: () => void;
    onLinked: (message: string) => void;
    onError: (message: string) => void;
}

function amount(value: number | null, currency: string | null): string {
    if (value === null || value === undefined) return '—';
    return `${value.toLocaleString('de-CH', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}${currency ? ` ${currency}` : ''}`;
}

/**
 * Pick the DocuWare document that belongs to a posted BC invoice, and link it.
 *
 * A SEARCH, NOT A CONFIRM BUTTON — and that is the design decision, not a nicety. The
 * candidate the filing check offers is frequently wrong: 40 of the 72 open cases were found by
 * CONTAINMENT, which is how posting 841758 gets offered 'KORR. RG 950033399' (a correction
 * document) and 841446 gets '8508603 VZ' (a prepayment). 'GRATIS' is a real invoice number in
 * this data and it matches 'GRATIS.' A dialog that only offered "accept" would be a machine
 * for making wrong links look reviewed.
 *
 * So the candidate is PRESELECTED and never preset. It arrives as the highlighted row of a
 * result list, with the document's own supplier, number, amount and filing date next to the
 * posting's, and a DocuWare deep link to open the PDF. Nothing is written until the reviewer
 * presses the button, and the row they press it on is the row that gets written.
 *
 * WHAT IT WARNS ABOUT BEFORE COMMITTING, because both are irreversible-looking surprises
 * otherwise:
 *   - the chosen document already links to another posting (the ordinary case for pattern E),
 *   - and the collective bar: from the second posting on, the order check stops reconciling
 *     that document against the booked invoice and falls back to the purchase order.
 */
export function FilingCheckLinkDialog({ row, onClose, onLinked, onError }: Props) {
    const [search, setSearch] = useState('');
    const [amountFilter, setAmountFilter] = useState('');
    const [docs, setDocs] = useState<FilingCheckDocument[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    // Preselected, NOT preset: it is the highlighted row, and nothing is written until the
    // button below is pressed.
    const [selected, setSelected] = useState<number | null>(row.dwDocumentId);

    const load = useCallback(async (term: string, amt: string) => {
        setLoading(true);
        try {
            const items = await docuwareFilingCheckApi.searchDocuments({
                companyCode: row.companyCode,
                search: term,
                amount: amt,
                pinnedDocumentId: row.dwDocumentId,
            });
            setDocs(items);
        } catch (err) {
            onError(err instanceof Error ? err.message : 'Document search failed');
        } finally {
            setLoading(false);
        }
    }, [row.companyCode, row.dwDocumentId, onError]);

    // First open: the supplier invoice number is the strongest thing we know about the
    // posting, so the list starts where a reviewer would have typed anyway.
    useEffect(() => {
        void load(row.vendorInvoiceNo ?? '', '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const chosen = docs.find(d => d.documentId === selected) ?? null;

    const submit = async () => {
        if (!chosen) return;
        setSaving(true);
        try {
            const result = await docuwareFilingCheckApi.link(
                row.companyCode, row.bcDocumentNo, chosen.documentId
            );
            const parts = [
                `${row.bcDocumentNo} linked to document ${chosen.documentId}`,
            ];
            // Every side effect the endpoint reports is repeated to the reviewer. A change
            // they did not ask for and cannot see is the one that erodes trust in the button.
            if (result.becamePrimary) parts.push('it is now the document’s primary posting');
            if (result.becameCollective) {
                parts.push(
                    'the document now covers several postings, so the order check compares it '
                    + 'against the purchase order instead of the booked invoice'
                );
            }
            parts.push(
                result.orderCheckRefreshed === null
                    ? 'the order check could not be re-run (logged)'
                    : `order check re-run for ${result.orderCheckRefreshed} document(s)`
            );
            onLinked(parts.join(' · '));
        } catch (err) {
            onError(err instanceof Error ? err.message : 'Linking failed');
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
            <div className="mt-8 w-full max-w-5xl rounded-lg bg-white shadow-xl">
                <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-3">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">
                            Link a DocuWare document to posting {row.bcDocumentNo}
                        </h3>
                        <p className="mt-0.5 text-xs text-gray-500">
                            {row.vendorName ?? '—'} · invoice{' '}
                            <span className="font-mono">{row.vendorInvoiceNo ?? '—'}</span> ·{' '}
                            {amount(row.netAmount, row.currency)} · posted{' '}
                            {row.postingDate ? formatDate(row.postingDate) : '—'}
                        </p>
                    </div>
                    <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="flex flex-wrap items-end gap-3 border-b border-gray-100 px-5 py-3">
                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-gray-600">
                            Supplier or invoice number
                        </span>
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') void load(search, amountFilter); }}
                            placeholder={row.vendorInvoiceNo ?? 'Search DocuWare'}
                            className="h-9 w-80 rounded border border-gray-300 px-2 text-sm"
                        />
                    </label>
                    <label className="block">
                        {/* A tolerance search, not equality: DocuWare stores the gross and this
                            row shows the net, so the two differ by the VAT on most rows. */}
                        <span className="mb-1 block text-xs font-medium text-gray-600">
                            Amount (±1%)
                        </span>
                        <input
                            value={amountFilter}
                            onChange={e => setAmountFilter(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') void load(search, amountFilter); }}
                            placeholder={row.netAmount === null ? '' : String(row.netAmount)}
                            className="h-9 w-32 rounded border border-gray-300 px-2 text-right text-sm"
                        />
                    </label>
                    <button
                        onClick={() => void load(search, amountFilter)}
                        disabled={loading}
                        className="inline-flex h-9 items-center gap-2 rounded border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        {loading
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <Search className="h-4 w-4" />}
                        Search
                    </button>
                    <span className="text-xs text-gray-400">
                        {docs.length} document{docs.length === 1 ? '' : 's'} · {row.companyCode} only
                    </span>
                </div>

                <div className="max-h-[45vh] overflow-auto">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 z-10 bg-gray-50 text-xs uppercase tracking-wide shadow-[inset_0_-1px_0_rgb(229,231,235)]">
                            <tr>
                                <th className="w-10 px-3 py-2"></th>
                                <th className="px-3 py-2 text-left font-medium text-gray-500">Invoice no.</th>
                                <th className="px-3 py-2 text-left font-medium text-gray-500">Supplier</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-500">Amount</th>
                                <th className="px-3 py-2 text-left font-medium text-gray-500">Filed</th>
                                <th className="px-3 py-2 text-left font-medium text-gray-500">Already linked to</th>
                                <th className="px-3 py-2 text-left font-medium text-gray-500">DocuWare</th>
                            </tr>
                        </thead>
                        <tbody>
                            {docs.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={7} className="px-5 py-8 text-center text-sm text-gray-500">
                                        No document matches. Widen the search — the invoice number
                                        on the posting is not always the one on the filed document.
                                    </td>
                                </tr>
                            )}
                            {docs.map(d => {
                                const isCandidate = d.documentId === row.dwDocumentId;
                                const active = d.documentId === selected;
                                return (
                                    <tr
                                        key={d.documentId}
                                        onClick={() => setSelected(d.documentId)}
                                        className={`cursor-pointer border-b border-gray-100 ${
                                            active ? 'bg-blue-50' : 'hover:bg-gray-50'
                                        }`}
                                    >
                                        <td className="px-3 py-2">
                                            <input
                                                type="radio"
                                                checked={active}
                                                onChange={() => setSelected(d.documentId)}
                                                className="h-4 w-4"
                                            />
                                        </td>
                                        <td className="px-3 py-2 font-mono text-xs text-gray-800">
                                            {d.invoiceNumber ?? '—'}
                                            {isCandidate && (
                                                <span
                                                    className="ml-1.5 rounded bg-amber-100 px-1 text-[10px] font-medium uppercase text-amber-700"
                                                    title={
                                                        row.matchBasis === 'contains'
                                                            ? 'Suggested by a CONTAINMENT match — one number contains the '
                                                              + 'other. Weaker evidence: check the number and amount before '
                                                              + 'accepting it.'
                                                            : 'Suggested by the filing check, on the invoice number itself.'
                                                    }
                                                >
                                                    {row.matchBasis === 'contains' ? 'suggested (loose)' : 'suggested'}
                                                </span>
                                            )}
                                        </td>
                                        <td className="max-w-[18rem] truncate px-3 py-2 text-gray-700"
                                            title={d.supplier ?? undefined}>
                                            {d.supplier ?? '—'}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-700">
                                            {amount(d.amount, d.currency)}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                                            {d.storedAt ? formatDate(d.storedAt) : '—'}
                                        </td>
                                        <td className="px-3 py-2 font-mono text-xs text-gray-600">
                                            {d.bcPostings ?? <span className="text-gray-300">— not linked</span>}
                                        </td>
                                        <td className="px-3 py-2">
                                            {d.documentUrl ? (
                                                <a
                                                    href={d.documentUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={e => e.stopPropagation()}
                                                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                                                >
                                                    open <ExternalLink className="h-3 w-3" />
                                                </a>
                                            ) : (
                                                <span className="text-xs text-gray-300">—</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="space-y-2 border-t border-gray-200 px-5 py-3">
                    {chosen && chosen.postingCount > 0 && (
                        <p className="flex items-start gap-1.5 text-xs text-amber-700">
                            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
                            <span>
                                This document already covers {chosen.postingCount} posting
                                {chosen.postingCount === 1 ? '' : 's'} ({chosen.bcPostings}).
                                Linking {row.bcDocumentNo} as well makes it a collective invoice:
                                the Order Check then compares it against the purchase order
                                instead of the booked invoice, because no single posting is the
                                whole invoice any more.
                            </span>
                        </p>
                    )}
                    <div className="flex items-center justify-between gap-3">
                        <span className="text-xs text-gray-500">
                            {chosen
                                ? <>Writes one manual link. It survives the matcher unless the
                                    matcher re-matches this document on both keys — and that is
                                    recorded and shown here.</>
                                : 'Select a document.'}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={onClose}
                                className="h-9 rounded border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => void submit()}
                                disabled={!chosen || saving}
                                className="inline-flex h-9 items-center gap-2 rounded bg-blue-600 px-3 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40"
                            >
                                {saving
                                    ? <Loader2 className="h-4 w-4 animate-spin" />
                                    : <Link2 className="h-4 w-4" />}
                                Link this document
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
